import _ from 'lodash'
import { Mesh, MeshHierarchyStructure, MeshOptions, meshV2, structure2mesh } from '@wix/thunderbolt-mesh'
import { Structure, Style } from '@wix/thunderbolt-becky-types'
import { BeckyFunctionLibrary, MeshResult } from '../../types'
import {
	AutoHeightComponents,
	DataForMeshCalculation,
	MeshComponent,
	MeshSelectorsOptions,
	OverridesData,
} from './mesh'
import { POSITION_ABSOLUTE_IN_MESH, skinPartId } from './meshConstants'
import { getMeshOverrides } from './meshOverrides'
import { getMeshStyles } from './meshStyle'

export const getStylesAndWedgesAndRotated = (
	compId: string,
	meshData: Mesh,
	autoHeightComps: AutoHeightComponents,
	options?: MeshSelectorsOptions
): MeshResult => {
	const children = _.get(meshData, 'structure.children[0].children[0].children', []) as Array<MeshHierarchyStructure>
	const wedges = children.map((child) => child.id).filter((id) => id.includes('wedge'))
	const rotatedComponents = children
		.filter((child) => child.id.includes('rotated-wrapper'))
		.map((rotatedWrapper) => rotatedWrapper.children[0].id)
	const stylesMap = getMeshStyles(compId, meshData.styles, rotatedComponents, autoHeightComps, options)
	return { stylesMap, wedges, rotatedComponents }
}

const getMeshContainer = (
	compMeshData: DataForMeshCalculation,
	components: Array<MeshComponent>,
	overridesData: OverridesData
) => {
	const {
		compStructure,
		compData,
		dataForColumn = {},
		isMesh2ExperimentOpen = false,
		childIdToMeshSingleLayoutData = {},
	} = compMeshData
	const { layout, metaData: compMetaData } = compStructure
	const { props: compProp = {}, style = {}, data = {}, meshSingleLayoutData } = compData
	const compSkin = (style as Style).skin
	const skinName = (compSkin || compStructure.skin) as string
	const filteredComps = components.filter((child) => !child.isFixed)

	const width =
		isMesh2ExperimentOpen && meshSingleLayoutData!.componentLayout.width
			? meshSingleLayoutData!.componentLayout.width.value
			: layout.width
	const height =
		isMesh2ExperimentOpen && meshSingleLayoutData!.componentLayout
			? meshSingleLayoutData!.componentLayout.height?.value ||
			  meshSingleLayoutData!.componentLayout.minHeight?.value
			: layout.height

	const overrides = getMeshOverrides({
		...overridesData,
		compProp,
		compData: data,
		dataForColumn,
		components: filteredComps,
		componentType: compStructure.componentType,
		compId: compStructure.id,
		compMetaData,
		skinName,
	})
	return {
		width,
		height,
		id: `${compStructure.id}${skinPartId}`,
		components: filteredComps,
		adjustingComp: null,
		childIdToMeshSingleLayoutData,
		isMesh2ExperimentOpen,
		...overrides,
	}
}

const removeMeshGridWrapper = (meshData: Mesh): Mesh => {
	const gridWrapperId = _.get(meshData.structure, ['children', '0', 'id'])

	if (!gridWrapperId) {
		// empty container
		return meshData
	}

	_.assign(meshData.styles[meshData.structure.id], meshData.styles[gridWrapperId])
	delete meshData.styles[gridWrapperId]

	return meshData
}

export const getMeshData = (
	compMeshData: DataForMeshCalculation,
	childComponents: Array<MeshComponent>,
	overridesData: OverridesData,
	meshOptions: MeshOptions = {}
): Mesh => {
	const container = getMeshContainer(compMeshData, childComponents, overridesData)
	if (compMeshData.isMesh2ExperimentOpen) {
		// @ts-ignore
		return removeMeshGridWrapper(meshV2(container))
	} else {
		// @ts-ignore - Remove once santa-mesh is updated
		const meshData = structure2mesh(container, {
			...meshOptions,
			isRepeatSupported: true,
		})
		return removeMeshGridWrapper(meshData)
	}
}

export const getMeshComponents = (
	compId: string,
	structure: Structure,
	absoluteComps: Array<string>
): Array<MeshComponent> => {
	const compStructure = structure[compId]

	return (compStructure.components || []).map((childCompId) => {
		const childStructure = structure[childCompId]
		const { x, y, width, height, docked, fixedPosition, rotationInDegrees } = childStructure.layout

		return {
			id: childCompId,
			type: childStructure.componentType,
			left: x,
			top: y,
			width,
			height,
			docked: docked || {},
			isFixed: Boolean(fixedPosition),
			rotationInDegrees,
			absolute:
				POSITION_ABSOLUTE_IN_MESH.includes(childStructure.componentType) || absoluteComps.includes(childCompId),
		} as MeshComponent
	})
}

const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b)

const cache = new WeakMap<DataForMeshCalculation['compStructure'], MeshResult>()
export const getMeshResult: BeckyFunctionLibrary['getMeshResult'] = (
	compMeshData,
	overridesData,
	absoluteComps,
	autoHeightComps
) => {
	const compId = compMeshData.compStructure.id
	const childComponents = getMeshComponents(compId, compMeshData.compAndChildrenStructure!, absoluteComps)
	const fixedComponents = childComponents.filter((comp) => comp.isFixed).map((comp) => comp.id)
	const meshResult: MeshResult = {
		fixedComponents,
		...getStylesAndWedgesAndRotated(
			compId,
			getMeshData(compMeshData, childComponents, overridesData),
			autoHeightComps,
			{ isChildOfRepeater: compMeshData.isChildOfRepeater }
		),
	}
	const prevMeshResult = cache.get(compMeshData.compStructure)
	if (!prevMeshResult || !isEqual(meshResult, prevMeshResult)) {
		cache.set(compMeshData.compStructure, meshResult)
		return meshResult
	}

	return prevMeshResult
}
